import { service } from '@ember/service';
import type { AsyncBelongsTo, AsyncHasMany } from '@ember-data/model';
import Model, { attr, belongsTo, hasMany } from '@ember-data/model';
import { dropTask } from 'ember-concurrency';
import type AnnouncementAttachmentModel from 'garaje/models/announcement-attachment';
import type AnnouncementAudienceModel from 'garaje/models/announcement-audience';
import type AnnouncementChannelModel from 'garaje/models/announcement-channel';
import type AnnouncementRecipientModel from 'garaje/models/announcement-recipient';
import type AnnouncementResponseOptionModel from 'garaje/models/announcement-response-option';
import type CompanyModel from 'garaje/models/company';
import type EmergencyNotificationModel from 'garaje/models/emergency-notification';
import type EmployeeModel from 'garaje/models/employee';
import type EmployeeGroupModel from 'garaje/models/employee-group';
import type LocationModel from 'garaje/models/location';
import type AjaxService from 'garaje/services/ajax';
import type FlashMessagesService from 'garaje/services/flash-messages';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import urlBuilder from 'garaje/utils/url-builder';

import type TakeoverModel from './takeover';

export interface AnnouncementStatistics {
  employee_recipients: number;
  visitor_recipients: number;
  sent_employee_recipients: number;
  sent_visitor_recipients: number;
  marked_as_safe_recipients: number;
  safe_recipients: number;
  need_help_recipients: number;
  no_response_recipients: number;
}

export default class AnnouncementModel extends Model {
  @service declare flashMessages: FlashMessagesService;
  @service declare ajax: AjaxService;

  // Relationships for both old and new
  @belongsTo('company', { async: true, inverse: null }) declare company: AsyncBelongsTo<CompanyModel>;
  @belongsTo('location', { async: true, inverse: 'announcements' }) declare location: AsyncBelongsTo<LocationModel>;

  //Relationships for new endpoint
  @belongsTo('employee', { async: true, inverse: null }) declare creatorEmployee: AsyncBelongsTo<EmployeeModel>;
  @belongsTo('emergency-notification', { async: true, inverse: 'announcement' })
  declare emergencyNotification: AsyncBelongsTo<EmergencyNotificationModel>;
  @hasMany('announcement-recipient', { async: true, inverse: 'announcement' })
  declare announcementRecipients: AsyncHasMany<AnnouncementRecipientModel>;
  @hasMany('announcement-channel', { async: true, inverse: 'announcement' })
  declare announcementChannels: AsyncHasMany<AnnouncementChannelModel>;
  @hasMany('announcement-audience', { async: true, inverse: 'announcement' })
  declare announcementAudiences: AsyncHasMany<AnnouncementAudienceModel>;
  @hasMany('employee-group', { async: true, inverse: null }) declare employeeGroups: AsyncHasMany<EmployeeGroupModel>;
  @hasMany('takeovers', { async: true, inverse: null })
  declare takeovers: AsyncHasMany<TakeoverModel>;
  @hasMany('announcement-response-option', { async: true, inverse: 'announcement' })
  declare announcementResponseOptions: AsyncHasMany<AnnouncementResponseOptionModel>;
  @hasMany('announcement-attachment', { async: true, inverse: null })
  declare announcementAttachments: AsyncHasMany<AnnouncementAttachmentModel>;

  // Common attributes between old and new api
  @attr('string') declare title: string;
  @attr('string') declare description: string;
  @attr('string') declare link: string;
  @attr('boolean') declare shouldSendPushNotification: boolean;
  @attr('date') declare createdAt: Date;

  // Attributes for old api only remove after sunset
  @attr('number') declare numSeen: number;
  @attr('boolean') declare isArchived: boolean;

  // Attributes for new api end point /v3
  @attr('string') declare message: string;
  @attr('boolean') declare isEmergencyNotification: boolean;
  @attr('date') declare archivedAt: Date;
  @attr('boolean') declare archived: boolean;
  @attr('object') declare statistics: AnnouncementStatistics;

  sendPushNotificationTask = dropTask(async () => {
    const url = urlBuilder.rms.sendAnnouncementNotification(this.id);
    try {
      await this.ajax.request(url, {
        // @ts-ignore accept is not a valid attribute for request options
        accept: 'application/vnd.api+json',
        type: 'POST',
      });
      this.flashMessages.showAndHideFlash('success', 'Message sent!');
    } catch (e) {
      const errorText = parseErrorForDisplay(e);
      this.flashMessages.showFlash('error', errorText);
    }
  });
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    announcement: AnnouncementModel;
  }
}
